import React, { useState } from 'react';
import { Facebook, Twitter, Linkedin, Link, Check } from 'lucide-react';
import { shareContent, copyToClipboard, cn } from '@/lib/utils';
import type { Post } from '@/types/database';

interface ShareMenuProps {
  post: Post;
  onClose: () => void;
}

export function ShareMenu({ post, onClose }: ShareMenuProps) {
  const [copied, setCopied] = useState(false);
  const postUrl = `${window.location.origin}/post/${post.id}`;
  const shareText = post.content.slice(0, 100) + (post.content.length > 100 ? '...' : '');

  const handleShare = async () => {
    const shared = await shareContent(
      'Check out this post on RoyaltySuite',
      shareText,
      postUrl
    );
    
    if (!shared) {
      // Fallback to copying to clipboard
      handleCopyLink();
    }
  };

  const handleCopyLink = async () => {
    const success = await copyToClipboard(postUrl);
    if (success) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const socialShareLinks = [
    {
      name: 'Facebook',
      icon: Facebook,
      url: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(postUrl)}`,
      color: 'hover:text-[#1877F2] dark:hover:text-[#1877F2]',
    },
    {
      name: 'Twitter',
      icon: Twitter,
      url: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(postUrl)}`,
      color: 'hover:text-[#1DA1F2] dark:hover:text-[#1DA1F2]',
    },
    {
      name: 'LinkedIn',
      icon: Linkedin,
      url: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(postUrl)}`,
      color: 'hover:text-[#0A66C2] dark:hover:text-[#0A66C2]',
    },
  ];

  return (
    <div className="absolute bottom-full left-0 mb-2 bg-white dark:bg-dark-paper rounded-lg shadow-lg border dark:border-dark-border p-2 min-w-[200px] animate-fade-in">
      <div className="p-2 space-y-2">
        {/* Native share button */}
        <button
          onClick={handleShare}
          className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-dark-elevated transition-colors text-left text-gray-700 dark:text-gray-200"
        >
          <Link className="w-5 h-5 text-gray-600 dark:text-gray-400" />
          <span className="text-sm font-medium">Share post</span>
        </button>

        {/* Copy link button */}
        <button
          onClick={handleCopyLink}
          className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-dark-elevated transition-colors text-left text-gray-700 dark:text-gray-200"
        >
          {copied ? (
            <Check className="w-5 h-5 text-green-600 dark:text-green-500" />
          ) : (
            <Link className="w-5 h-5 text-gray-600 dark:text-gray-400" />
          )}
          <span className="text-sm font-medium">
            {copied ? 'Copied!' : 'Copy link'}
          </span>
        </button>

        <div className="h-px bg-gray-200 dark:bg-dark-border my-2" />

        {/* Social share buttons */}
        {socialShareLinks.map((social) => {
          const Icon = social.icon;
          return (
            <a
              key={social.name}
              href={social.url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => {
                e.preventDefault();
                window.open(
                  social.url,
                  'share',
                  'width=550,height=450,toolbar=no,menubar=no'
                );
              }}
              className={cn(
                "w-full flex items-center gap-3 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-dark-elevated transition-colors text-gray-700 dark:text-gray-200",
                social.color
              )}
            >
              <Icon className="w-5 h-5" />
              <span className="text-sm font-medium">Share on {social.name}</span>
            </a>
          );
        })}
      </div>
    </div>
  );
}